import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import CreateRoundedIcon from '@mui/icons-material/CreateRounded'
import useStyles from './_styles'
import { PJItem, TransmissionPJProps, PJ_CONSTANTS, TransmissionPJModalState } from './_props'
import CardContainerPJ from './CardContainerPJ'
import DialogRename from './dialogRename'
import DialogConfirmDelete from './dialogConfirmDelete'
import { AttachFileIcon, UnderlineIcon, UploadFileIcon } from 'apps/ids/src/app/assets/icons/icons'
import {
    getMediaTypeFromFile,
    toObjectUrl,
    buildAnswer,
    restoreItemsFromInitialValue,
} from 'apps/ids/src/app/components/utils/file'
import { useDropzone } from 'react-dropzone'
import { useAppDispatch } from '../../../../store'
import { displaySnackbar } from '../../../../actions/main'
import TransmissionPJDropArea from './TransmissionPJDropArea'
import ErrorIcon from '@mui/icons-material/Error'
import { defaultPalette } from '../../../../theme/idsTheme'
import { isFileSizeValid, MAX_FILES, modalStateDefaultValues, RENAME_DIALOG_MAX_LENGTH } from './_utils'
import MediaViewer from '../../../Common/MediaViewer'
import Loader from '../../../Common/Loader/Loader'
import { isMobileOrTabletBrowser } from '../../../../utils/commonUtils'
import {
    useDeleteIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ,
    getIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJMediaId,
    useGetIdsHealthcareservicesHcsIdTransmissionsTransmissionId,
    getGetIdsHealthcareservicesHcsIdTransmissionsTransmissionIdQueryKey,
    usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ,
    usePutIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ,
} from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'

const TransmissionPJ = ({ question, initialValue, onAnswer }: TransmissionPJProps) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const currentHcs = useCurrentHcs()
    const queryClient = useQueryClient()
    const [items, setItems] = useState<PJItem[]>(() => restoreItemsFromInitialValue(initialValue))
    const [confirmDelete, setConfirmDelete] = useState<TransmissionPJModalState>(modalStateDefaultValues)
    const [renaming, setRenaming] = useState<TransmissionPJModalState>(modalStateDefaultValues)
    const [preview, setPreview] = useState<TransmissionPJModalState>(modalStateDefaultValues)
    const [isEmptyDropzoneError, setIsEmptyDropzoneError] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const itemsLengthRef = useRef(items.length)
    itemsLengthRef.current = items.length
    const itemsRef = useRef(items)
    itemsRef.current = items
    const { transmissionId } = useParams()

    const { data: transmissionDetail } = useGetIdsHealthcareservicesHcsIdTransmissionsTransmissionId(
        currentHcs.id,
        transmissionId ?? '',
        {
            // read-only lookup of the media ids: must never trigger a refetch of the page's own query
            query: {
                enabled: Boolean(transmissionId),
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                refetchOnReconnect: false,
            },
        },
    )

    const { mutateAsync: uploadPJ } = usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ()
    const { mutateAsync: renamePJ } = usePutIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ()
    const { mutateAsync: deletePJ } = useDeleteIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJ()

    useEffect(() => {
        return () => {
            itemsRef.current.forEach((item) => URL.revokeObjectURL(item.url))
        }
    }, [])

    useEffect(() => {
        let cancelled = false

        const loadRemoteItem = async (item: PJItem) => {
            try {
                const mediaId =
                    item.mediaId ??
                    decodeURIComponent((item.remoteUrl as string).split(/[?#]/)[0].split('/').pop() ?? '')
                const blob = await getIdsHealthcareservicesHcsIdTransmissionsTransmissionIdPJMediaId(
                    currentHcs.id,
                    transmissionId ?? '',
                    mediaId,
                )
                if (cancelled) return
                const url = URL.createObjectURL(blob)
                setItems((previous) =>
                    previous.map((current) => (current.id === item.id ? { ...current, url } : current)),
                )
            } catch (e) {
                console.error({ e })
            }
        }

        const loadRemoteItems = async () => {
            await Promise.all(itemsRef.current.filter((item) => item.remoteUrl && !item.url).map(loadRemoteItem))
        }
        loadRemoteItems()

        return () => {
            cancelled = true
        }
    }, [])

    const photos = useMemo(() => items.filter((item) => item.mediaType === 'picture'), [items])
    const documents = useMemo(() => items.filter((item) => item.mediaType === 'document'), [items])
    const videos = useMemo(() => items.filter((item) => item.mediaType === 'video'), [items])
    const hasMedia = items.length > 0

    const dropzoneAccept = useMemo(
        () => ({
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'application/pdf': ['.pdf'],
            'video/quicktime': ['.mov'],
            'video/mp4': ['.mp4'],
        }),
        [],
    )

    const showFileValidationError = () => {
        if (!hasMedia) {
            setIsEmptyDropzoneError(true)
            return
        }

        setIsEmptyDropzoneError(false)

        dispatch(displaySnackbar('error', "Une pièce jointe n'a pas pu être importée (format ou taille invalide)"))
    }

    const findMedia = (item: PJItem) => {
        if (!item.remoteUrl) return undefined
        const stripQuery = (url: string) => url.split(/[?#]/)[0]
        const medias = [
            ...(transmissionDetail?.pictures ?? []),
            ...(transmissionDetail?.videos ?? []),
            ...(transmissionDetail?.documents ?? []),
        ] as { id?: string; name?: string; url: string }[]
        return medias.find((m) => stripQuery(m.url) === stripQuery(item.remoteUrl as string))
    }

    const resolveMediaId = (item: PJItem) => item.mediaId || findMedia(item)?.id || undefined

    useEffect(() => {
        if (!transmissionDetail) return
        setItems((previous) => {
            let changed = false
            const next = previous.map((item) => {
                const name = findMedia(item)?.name
                if (!name || name === item.name) return item
                changed = true
                return { ...item, name }
            })
            return changed ? next : previous
        })
    }, [transmissionDetail])

    const updateCachedMedia = (mediaId: string, name: string | null) => {
        queryClient.setQueryData(
            getGetIdsHealthcareservicesHcsIdTransmissionsTransmissionIdQueryKey(currentHcs.id, transmissionId ?? ''),
            (detail: any) => {
                if (!detail) return detail
                const update = (medias: any[] = []) =>
                    name === null
                        ? medias.filter((media) => media.id !== mediaId)
                        : medias.map((media) => (media.id === mediaId ? { ...media, name } : media))
                return {
                    ...detail,
                    pictures: update(detail.pictures),
                    videos: update(detail.videos),
                    documents: update(detail.documents),
                }
            },
        )
    }

    const notifyAnswer = (nextItems: PJItem[]) => {
        onAnswer?.(buildAnswer(nextItems))
    }

    const addFiles = async (files: File[]) => {
        if (files.length === 0 || isUploading) return

        const remaining = MAX_FILES - itemsLengthRef.current

        if (remaining <= 0 || files.length > remaining) {
            showFileValidationError()
            return
        }

        const hasInvalidSize = files.some((file) => !isFileSizeValid(file))

        if (hasInvalidSize) {
            showFileValidationError()
            return
        }

        setIsEmptyDropzoneError(false)
        setIsUploading(true)

        try {
            const uploadedItems: PJItem[] = []
            for (const file of files) {
                const uploaded = await uploadPJ({
                    hcsId: currentHcs.id,
                    transmissionId: transmissionId ?? '',
                    data: {
                        File: file,
                        TransmissionStepId: question.id?.replace('W.Q.', 'W.A.'),
                        Name: file.name,
                    },
                })

                const mediaType =
                    uploaded.documentType === 'documents'
                        ? 'document'
                        : uploaded.documentType === 'pictures'
                        ? 'picture'
                        : uploaded.documentType === 'videos'
                        ? 'video'
                        : getMediaTypeFromFile(file)

                uploadedItems.push({
                    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
                    file,
                    url: toObjectUrl(file),
                    remoteUrl: uploaded.documentUrl,
                    mediaId: uploaded.documentId,
                    name: file.name,
                    mediaType,
                } as PJItem)
            }

            const currentItems = itemsRef.current
            const combined = [...currentItems, ...uploadedItems].slice(0, MAX_FILES)

            setItems(combined)
            notifyAnswer(combined)
        } catch {
            dispatch(displaySnackbar('error', "Une pièce jointe n'a pas pu être importée"))
        } finally {
            setIsUploading(false)
        }
    }

    const { getRootProps, getInputProps, isDragAccept, isDragReject, open } = useDropzone({
        accept: dropzoneAccept,
        maxFiles: MAX_FILES,
        noClick: true,
        noKeyboard: true,
        onDrop: (acceptedFiles, rejections) => {
            const hasInvalidSize = acceptedFiles.some((file) => !isFileSizeValid(file))
            const remaining = MAX_FILES - itemsLengthRef.current
            const exceedsCapacity = acceptedFiles.length > remaining

            if (rejections.length > 0 || hasInvalidSize || exceedsCapacity) {
                showFileValidationError()
                return
            }

            addFiles(acceptedFiles)
        },
    })

    const dropzoneStyle = useMemo(
        () => ({
            borderColor: isDragAccept ? '#00e676' : isDragReject ? '#ff1744' : undefined,
        }),
        [isDragAccept, isDragReject],
    )

    const emptyDropzoneStyle = useMemo(
        () => ({
            ...dropzoneStyle,
            ...(isEmptyDropzoneError
                ? {
                      borderColor: defaultPalette.error.main,
                      background: '#FFF5F7',
                  }
                : {}),
        }),
        [dropzoneStyle, isEmptyDropzoneError],
    )

    const previewTarget = preview.targetId ? items.find((item) => item.id === preview.targetId) : undefined
    const previewIndex = previewTarget ? photos.findIndex((item) => item.id === previewTarget.id) : -1

    const hasPrevious = () => previewIndex > 0
    const hasNext = () => previewIndex !== -1 && previewIndex < photos.length - 1

    const handlePrevious = () => {
        if (!hasPrevious()) return
        setPreview({ open: true, targetId: photos[previewIndex - 1].id })
    }

    const handleNext = () => {
        if (!hasNext()) return
        setPreview({ open: true, targetId: photos[previewIndex + 1].id })
    }

    const handleMediaPreview = (targetId: string) => {
        const target = items.find((item) => item.id === targetId)
        if (!target?.url) return

        if (target.mediaType === 'document') {
            window.open(target.url, '_blank', 'noopener,noreferrer')
            return
        }

        setPreview({ open: true, targetId })
    }

    const toolbarEditButton = isMobileOrTabletBrowser() ? (
        <IconButton
            onClick={() =>
                previewTarget && setRenaming({ open: true, targetId: previewTarget.id, name: previewTarget.name })
            }
            size="large"
        >
            <CreateRoundedIcon />
        </IconButton>
    ) : (
        <Button
            onClick={() =>
                previewTarget && setRenaming({ open: true, targetId: previewTarget.id, name: previewTarget.name })
            }
        >
            <CreateRoundedIcon />
            <span>Editer</span>
        </Button>
    )
    const mediaViewerToolbarButtons = [toolbarEditButton]

    const showTitle = 'Pièces jointes'

    const mediaSections = [
        { title: 'Photos', list: photos },
        { title: 'Documents', list: documents },
        { title: 'Vidéos', list: videos },
    ]

    const handleDropzoneClick = (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        open()
    }

    const renderEmptyDropzone = () => (
        <TransmissionPJDropArea
            rootProps={getRootProps({
                onClick: handleDropzoneClick,
            })}
            className={classes.dropZone}
            style={emptyDropzoneStyle}
        >
            <Box className={classes.dropHeader}>
                <Box className={classes.dropIcon}>
                    <UploadFileIcon />
                </Box>
                <Box className={classes.dropText}>
                    <Typography className={classes.dropMainText}>
                        <span className={classes.containerTextClick}>
                            Cliquer pour rechercher
                            <UnderlineIcon className={classes.underlineSvg} />
                        </span>
                        {' ou glisser-déposer un fichier'}
                    </Typography>
                    {isEmptyDropzoneError && (
                        <Box className={classes.dropErrorRow}>
                            <ErrorIcon className={classes.dropErrorIcon} />
                            <Typography className={classes.dropErrorText}>Le fichier n'est pas supporté</Typography>
                        </Box>
                    )}
                    <Typography
                        variant="body2"
                        className={isEmptyDropzoneError ? classes.dropSubTextError : classes.dropSubText}
                    >
                        {'Format JPG, PNG, MOV, MP4 ou PDF (max. 5 Mo)'}
                    </Typography>
                </Box>
            </Box>
        </TransmissionPJDropArea>
    )

    const renderMediaSections = () => (
        <TransmissionPJDropArea
            rootProps={getRootProps({
                onClick: handleDropzoneClick,
            })}
            className={classes.containerZone}
            style={dropzoneStyle}
        >
            <Stack gap="1.5rem" className={classes.dropItemsContainer}>
                {mediaSections.map((section) => (
                    <CardContainerPJ
                        key={section.title}
                        title={section.title}
                        list={section.list}
                        onRenaming={(targetId, name) =>
                            setRenaming({
                                open: true,
                                targetId,
                                name: name.normalize('NFC').slice(0, RENAME_DIALOG_MAX_LENGTH),
                            })
                        }
                        onPreview={handleMediaPreview}
                        onConfirmDelete={(targetId) => setConfirmDelete({ open: true, targetId })}
                    />
                ))}
            </Stack>
        </TransmissionPJDropArea>
    )

    return (
        <Box>
            <Stack className={classes.containerCard}>
                <Box className={classes.headerRow}>
                    <Box>
                        <Typography className={classes.topTitle}>{showTitle}</Typography>
                        <Typography className={classes.topDescription}>
                            Importez jusqu'à 8 fichiers à la fois
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        className={classes.addButton}
                        startIcon={<AttachFileIcon />}
                        onClick={open}
                        disabled={isUploading}
                    >
                        Ajouter
                    </Button>
                </Box>
                <input
                    {...getInputProps({
                        className: classes.hiddenInput,
                        multiple: true,
                    })}
                />
                <Box position="relative">
                    {items.length === 0 ? renderEmptyDropzone() : renderMediaSections()}
                    {isUploading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap="0.75rem"
                            sx={{ background: 'rgba(255, 255, 255, 0.75)', zIndex: 1, borderRadius: 'inherit' }}
                        >
                            <Loader title="Chargement en cours..." progressSize="2.5rem" />
                        </Box>
                    )}
                </Box>
            </Stack>
            <DialogRename
                open={renaming.open}
                title={`Renommer le fichier`}
                value={renaming.name ?? ''}
                onClose={() => setRenaming({ open: false })}
                onChangeValue={(next) => setRenaming((p) => ({ ...p, name: next }))}
                onConfirm={async () => {
                    const targetId = renaming.targetId
                    const newName = renaming.name?.trim() ?? ''
                    if (!targetId || !newName) return
                    const target = itemsRef.current.find((i) => i.id === targetId)
                    if (!target) return
                    const mediaId = resolveMediaId(target)
                    if (!mediaId) {
                        dispatch(displaySnackbar('error', "La pièce jointe n'a pas pu être renommée"))
                        return
                    }
                    try {
                        await renamePJ({
                            hcsId: currentHcs.id,
                            transmissionId: transmissionId ?? '',
                            data: { mediaId, name: newName },
                        })
                    } catch {
                        dispatch(displaySnackbar('error', "La pièce jointe n'a pas pu être renommée"))
                        return
                    }
                    setItems((prev) => {
                        const next = prev.map((i) => (i.id === targetId ? { ...i, name: newName } : i))
                        notifyAnswer(next)
                        return next
                    })
                    setRenaming({ open: false })
                    updateCachedMedia(mediaId, newName)
                    if (preview.open) {
                        setPreview({ open: false })
                    }
                }}
                isConfirmDisabled={!renaming.name?.trim()}
            />
            <DialogConfirmDelete
                open={confirmDelete.open}
                onClose={() => setConfirmDelete({ open: false })}
                onConfirm={async () => {
                    const targetId = confirmDelete.targetId
                    if (!targetId) return
                    const target = itemsRef.current.find((i) => i.id === targetId)
                    if (!target) return
                    const mediaId = resolveMediaId(target)
                    if (!mediaId) {
                        dispatch(displaySnackbar('error', "La pièce jointe n'a pas pu être supprimée"))
                        return
                    }
                    try {
                        await deletePJ({
                            hcsId: currentHcs.id,
                            transmissionId: transmissionId ?? '',
                            params: { mediaId },
                        })
                    } catch {
                        dispatch(displaySnackbar('error', "La pièce jointe n'a pas pu être supprimée"))
                        return
                    }

                    setItems((prev) => {
                        const target = prev.find((i) => i.id === targetId)
                        if (target) URL.revokeObjectURL(target.url)
                        const next = prev.filter((i) => i.id !== targetId)
                        notifyAnswer(next)
                        return next
                    })
                    setConfirmDelete({ open: false })
                    updateCachedMedia(mediaId, null)
                }}
            />
            {preview.open && previewTarget?.url && (
                <MediaViewer
                    source={previewTarget.url}
                    hasNext={hasNext()}
                    hasPrevious={hasPrevious()}
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    mediaName={previewTarget.name}
                    toolbarButtons={mediaViewerToolbarButtons}
                    onClose={() => {
                        setPreview({ open: false })
                    }}
                />
            )}
        </Box>
    )
}

export default TransmissionPJ
